import type { RevisionMismatchResponse } from "@skilitics/nautilus-endpoint-definition";

// Avoid pulling in axios just for this
interface IAxiosError extends Error {
  code?: string;
  status?: number;
  response?: { data?: unknown };
}

const isAxiosError = (error: unknown): error is IAxiosError => {
  return error instanceof Error && error.name === "AxiosError";
};

// from axios pasted in
const AXIOS_ERROR_CODES = [
  "ERR_BAD_OPTION_VALUE",
  "ERR_BAD_OPTION",
  "ECONNABORTED",
  "ETIMEDOUT",
  "ERR_NETWORK",
  "ERR_FR_TOO_MANY_REDIRECTS",
  "ERR_DEPRECATED",
  "ERR_BAD_RESPONSE",
  "ERR_BAD_REQUEST",
  "ERR_CANCELED",
  "ERR_NOT_SUPPORT",
  "ERR_INVALID_URL",
] as const;

const AXIOS_ERROR_CODE = Object.freeze(Object.fromEntries(AXIOS_ERROR_CODES.map((code) => [code, code])));

export const getErrorDetails = (err: unknown, startedAt: Date) => {
  if (isConflictError(err)) {
    return {
      type: "conflict",
      startedAt,
      message: {
        en: "Conflict error occurred. Make sure you do not have the same module open in multiple tabs or browsers.",
      },
    };
  }

  if (isAxiosError(err)) {
    if (err.code === AXIOS_ERROR_CODE.ETIMEDOUT || err.code === AXIOS_ERROR_CODE.ECONNABORTED) {
      return {
        type: "timeout",
        startedAt,
        message: {
          en: "The request to the server timed out. Please try again.",
        },
      };
    }
    if (err.code === AXIOS_ERROR_CODE.ERR_NETWORK) {
      return {
        type: "network",
        startedAt,
        message: {
          en: "The request to the server failed due to a network error. Please try again.",
        },
      };
    }
  }

  // this is when the server says no, not when the server falls over or connectivity issues
  return {
    type: "unknown",
    startedAt,
    message: {
      en: "An error occurred with the request to the server. Please try again.",
    },
  };
};

export const shouldRetry = (err: unknown) => {
  // retry if connectivity or server error/timeout but not bad request etc
  // todo: handle fetch errors too, but you pretty much need the is-network-error package for reasons...
  return (
    isAxiosError(err) && (err.code === "ERR_NETWORK" || err.code === "ETIMEDOUT" || (err.status && err.status >= 500))
  );
};

export type ErrorDetails = ReturnType<typeof getErrorDetails>;

export const isConflictError = (err: unknown) =>
  err instanceof Error &&
  err.name === "AxiosError" &&
  "status" in err &&
  typeof err.status === "number" &&
  err.status === 409;

export const isNotFoundError = (err: unknown) => isAxiosError(err) && err.status === 404;

const getConflictResponseData = (err: unknown): RevisionMismatchResponse | undefined => {
  if (!isConflictError(err)) return undefined;
  const data = (err as IAxiosError).response?.data as RevisionMismatchResponse | undefined;
  if (data && typeof data === "object" && "current" in data) return data;
  return undefined;
};

/** Extracts current revision from a 409 RevisionMismatchError response, or undefined if not applicable. */
export const getConflictExpectedRevision = (err: unknown): number | undefined =>
  getConflictResponseData(err)?.current.revision;

/** Extracts current checksum from a 409 RevisionMismatchError response, or undefined if not present. */
export const getConflictChecksum = (err: unknown): number | undefined =>
  getConflictResponseData(err)?.current.checksum ?? undefined;
